import { Button } from '@/components/form/Button';
import { Product } from '@/services/api/contracts/catalog/types';
import { useCategories, useManufacturers } from '@/services/api/hooks/catalog';
import { useBulkCreateProducts, useProducts } from '@/services/api/hooks/product';
import { IncomingProduct } from '@/services/csv/helpers/parsers';
import produce from 'immer';
import { useCallback, useState } from 'react';
import { toProduct } from '../helpers/toProduct';

import { useControls } from '@/components/multistep/useMultistep';
import { useAddPriceAndMargin } from '@/services/api/hooks/margins';
import { useRegions } from '@/services/api/hooks/region';
import { useMap, useResult } from '@/services/csv/useCSVService';
import { toast } from 'sonner';
import { Result } from '../bulkUploadHelper';
import { Maps } from '../enum';
import { ExtendedBulkProduct, ProductReviewGrid } from './ProductReviewGrid';

const filterNew = (incomingProducts: IncomingProduct[], catalog: Product[]) => {
  const duplicates = incomingProducts.filter((incoming: IncomingProduct) => {
    return catalog.some((catalogItem: Product) => catalogItem.productId === incoming.ManufacturerSKU);
  });

  // Filter all existing products
  return incomingProducts.filter(
    (incoming: IncomingProduct) =>
      !duplicates.some((duplicate: IncomingProduct) => duplicate.ManufacturerSKU === incoming.ManufacturerSKU)
  );
};

export const ReviewStep = () => {
  const result = useResult<Result>();
  if (!result) throw Error('No result');

  const { skipToStep } = useControls();

  const [manufacturers] = useManufacturers();
  const [categories] = useCategories();
  const [regions] = useRegions();
  const [catalog] = useProducts();

  const bulkCreateProducts = useBulkCreateProducts();
  const addPriceAndMargin = useAddPriceAndMargin();

  const manufacturerMap = useMap(Maps.Manufacturer);
  const categoryMap = useMap(Maps.Category);
  const regionMap = useMap(Maps.Regions);

  const fullManufacturersMap: Record<string, string> = {
    ...manufacturerMap,
    ...manufacturers.reduce((acc, m) => ({ ...acc, [m.name]: m.id }), {})
  };

  const fullCategoriesMap: Record<string, string> = {
    ...categoryMap,
    ...categories.reduce((acc, m) => ({ ...acc, [m.name]: m.id }), {})
  };

  const fullRegionMap: Record<string, string> = {
    ...regionMap,
    ...regions.reduce((acc, m) => ({ ...acc, [m.code]: m.id }), {})
  };

  const IncomingProductToProduct = IncomingProduct.transform(
    toProduct(categories, fullCategoriesMap, manufacturers, fullManufacturersMap, regions, fullRegionMap)
  ).pipe(ExtendedBulkProduct);

  const [products, setProducts] = useState<ExtendedBulkProduct[]>(
    filterNew(result.data, catalog).map(product => IncomingProductToProduct.parse(product))
  );

  const editProduct = useCallback(
    (recipe: (products: ExtendedBulkProduct[]) => void) => {
      return setProducts(existing => produce(existing, recipe));
    },
    [setProducts]
  );

  if (products.length === 0) {
    skipToStep('update');
  }

  const finish = async () => {
    await bulkCreateProducts.mutateAsync({
      products: products.map(p => ({
        ...p,
        manufacturerId: p.manufacturer.id,
        categoryId: p.category.id,
        regionId: p.region.id
      }))
    });
    toast.success('Products uploaded');
    skipToStep('update');
  };

  return (
    <div className="flex flex-col gap-2">
      {result.data.length > 0 && <ProductReviewGrid products={products} editProduct={editProduct} />}
      <Button loading={bulkCreateProducts.isLoading || addPriceAndMargin.isLoading} onClick={finish}>
        {result.data.length == 0 ? 'No new products, click to continue' : 'Upload products'}
      </Button>
    </div>
  );
};
